/**
 * Preferência de canal — regra pura (AD-002): quatro assuntos × dois canais,
 * chaveada por ciclo. Zero React, zero rede.
 *
 * A trava NÃO é do front. Ela é desenhada aqui e reimposta na montagem do
 * payload, mas quem garante a obrigatoriedade é o produtor, que recalcula a
 * matriz a partir de `player_fields__c` (T-03 do design). Um PUT adulterado
 * grava o que quiser; a supressão continua correta.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ATENÇÃO — METADE DESTE ARQUIVO ESTÁ SEM CONSUMIDOR DESDE 2026-08-08 (AD-018).
 *
 * A interface de escolha de canal saiu do front por decisão do usuário: a
 * página `/minhas-preferencias` e o modal de aceite voltaram a ser só renúncia
 * de dinâmica. O componente e o serviço que chamavam estas funções foram
 * APAGADOS; a receita para trazê-los de volta está em `spec.md`, § "Código
 * removido em 2026-08-08".
 *
 * EM USO hoje:
 *   `CommunicationSubject`, `COMMUNICATION_SUBJECTS`, `SUBJECT_LABEL`
 *       → central de notificações (`pages/Notificacoes`), filtro por assunto
 *   `canRenounceDynamics` (e `DynamicsEligibility`)
 *       → Dashboard, modal de aceite e esta página, para decidir quem renuncia
 *
 * SEM CONSUMIDOR, guardado de propósito:
 *   `Channel`, `CHANNELS`, `LockState`, `PreferenceUser`, `SubjectChoice`,
 *   `CommunicationPreferenceDoc`, `resolveLock`, `preferenceDocId`,
 *   `buildPreferenceState`, `togglePreference`, `toPreferencePayload`
 *
 * Ficaram porque são puras, têm teste próprio e a UI volta. `resolveLock` em
 * particular é a matriz de travas de PREF-02, verificada com 12/12 mutantes na
 * Fase 1 — reescrevê-la a partir de prosa seria o caminho mais curto para
 * reintroduzir os erros que aquela verificação pegou. O produtor espelha a
 * mesma matriz no servidor e segue valendo, então as duas pontas não podem
 * divergir quando a UI voltar.
 *
 * Se a decisão virar "não volta", este bloco inteiro sai junto com os testes.
 * ────────────────────────────────────────────────────────────────────────────
 */

export type CommunicationSubject = 'reconhecimento' | 'realizacao_destaque' | 'acoes_ecoa' | 'dipes';

export type Channel = 'email' | 'teams';

/**
 * Três estados, não dois: travado-obrigatório e travado-não-participa significam
 * coisas opostas e impõem valores opostos ao documento gravado (D-17).
 */
export type LockState = 'free' | 'locked_mandatory' | 'locked_not_participant';

export interface DynamicsEligibility {
  isManager?: boolean;
  cadMissao?: number;
  tipoRo?: string;
}

/**
 * Renúncia de dinâmica é para quem participa das dinâmicas. As três condições
 * são as que antes escondiam a página inteira — agora escondem só esse bloco,
 * porque a página passou a servir também ao opt-out de canal, que é de todos.
 */
export function canRenounceDynamics(user: DynamicsEligibility): boolean {
  return user.cadMissao !== 0 && user.tipoRo !== 'GERENCIAL' && !user.isManager;
}

/** Os três primeiros campos são exatamente os de `canRenounceDynamics`: a trava de
 *  Realização Destaque usa a MESMA regra que decide se o bloco de renúncia aparece,
 *  não `is_manager` sozinho (PREF-02.2). */
export interface PreferenceUser extends DynamicsEligibility {
  isManager: boolean;
  ecoa: 'INTEGRANTE' | 'TUTOR' | 'COORDENADOR' | false;
}

export interface SubjectChoice {
  subject: CommunicationSubject;
  label: string;
  lock: LockState;
  lockReason?: string;
  email: boolean;
  teams: boolean;
}

export interface CommunicationPreferenceDoc {
  /** `chave_ciclo`, ex.: "F0175790_2S2026" */
  _id: string;
  player: string;
  cycle: string;
  email: Record<CommunicationSubject, boolean>;
  teams: Record<CommunicationSubject, boolean>;
  updated: number;
}

export const COMMUNICATION_SUBJECTS: CommunicationSubject[] = [
  'reconhecimento',
  'realizacao_destaque',
  'acoes_ecoa',
  'dipes',
];

export const CHANNELS: Channel[] = ['email', 'teams'];

/** Exportado para a central de notificações reusar os mesmos rótulos (Fase 2). */
export const SUBJECT_LABEL: Record<CommunicationSubject, string> = {
  reconhecimento: 'Reconhecimento',
  realizacao_destaque: 'Realização Destaque',
  acoes_ecoa: 'Ações Ecoa',
  dipes: 'Comunicados da DIPES',
};

const MANDATORY_REASON = 'Comunicação obrigatória do seu papel.';
const NOT_PARTICIPANT_REASON = 'Você não participa dessa dinâmica.';

const ECOA_MANDATORY_ROLES = ['TUTOR', 'COORDENADOR'];

/**
 * A matriz de PREF-02. Reconhecimento e DIPES são livres para todos, então a tela
 * nunca fica vazia por mais travas que o papel acumule.
 */
export function resolveLock(subject: CommunicationSubject, user: PreferenceUser): LockState {
  if (subject === 'realizacao_destaque' && !canRenounceDynamics(user)) return 'locked_mandatory';

  if (subject === 'acoes_ecoa') {
    if (user.ecoa === false) return 'locked_not_participant';
    if (ECOA_MANDATORY_ROLES.includes(user.ecoa)) return 'locked_mandatory';
  }

  return 'free';
}

/** O valor que a trava impõe. `null` quando o assunto é livre e o jogador decide. */
function lockedValue(lock: LockState): boolean | null {
  if (lock === 'locked_mandatory') return true;
  if (lock === 'locked_not_participant') return false;

  return null;
}

function lockReason(lock: LockState): string | undefined {
  if (lock === 'locked_mandatory') return MANDATORY_REASON;
  if (lock === 'locked_not_participant') return NOT_PARTICIPANT_REASON;

  return undefined;
}

/** Ausência do documento do ciclo significa "recebe tudo" (PREF-03.2). */
function savedValue(
  saved: Partial<CommunicationPreferenceDoc> | null | undefined,
  channel: Channel,
  subject: CommunicationSubject
): boolean {
  return saved?.[channel]?.[subject] !== false;
}

export function preferenceDocId(playerKey: string, cycle: string): string {
  return `${playerKey}_${cycle}`;
}

export function buildPreferenceState(
  saved: Partial<CommunicationPreferenceDoc> | null | undefined,
  user: PreferenceUser
): SubjectChoice[] {
  return COMMUNICATION_SUBJECTS.map(subject => {
    const lock = resolveLock(subject, user);
    const imposed = lockedValue(lock);

    return {
      subject,
      label: SUBJECT_LABEL[subject],
      lock,
      ...(lock === 'free' ? {} : { lockReason: lockReason(lock) }),
      email: imposed ?? savedValue(saved, 'email', subject),
      teams: imposed ?? savedValue(saved, 'teams', subject),
    };
  });
}

export function togglePreference(
  state: SubjectChoice[],
  subject: CommunicationSubject,
  channel: Channel,
  enabled: boolean
): SubjectChoice[] {
  return state.map(choice =>
    choice.subject === subject && choice.lock === 'free' ? { ...choice, [channel]: enabled } : choice
  );
}

/**
 * Segunda barreira de PREF-02.6: o valor da trava é reimposto na serialização,
 * mesmo que o estado chegue adulterado. A primeira é a UI, que nem renderiza o
 * assunto travado; a terceira é o produtor, que recalcula a matriz no servidor.
 *
 * A trava vem do `user`, não do estado: um assunto ausente do array (estado vazio
 * ou parcial) não tem trava a ler, e cair no default "recebe tudo" gravaria o
 * oposto do que a matriz impõe.
 */
export function toPreferencePayload(
  playerKey: string,
  cycle: string,
  state: SubjectChoice[],
  user: PreferenceUser,
  now: number
): CommunicationPreferenceDoc {
  const byChannel = (channel: Channel) =>
    COMMUNICATION_SUBJECTS.reduce((acc, subject) => {
      const imposed = lockedValue(resolveLock(subject, user));
      const chosen = state.find(item => item.subject === subject)?.[channel];

      acc[subject] = imposed ?? (chosen !== false);

      return acc;
    }, {} as Record<CommunicationSubject, boolean>);

  return {
    _id: preferenceDocId(playerKey, cycle),
    player: playerKey,
    cycle,
    email: byChannel('email'),
    teams: byChannel('teams'),
    updated: now,
  };
}
